import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, DownloadIcon, EditPen, QrscanIcon } from "assests";
import EmptyComponent from "components/Card/EmptyComponent";
import BreadCrumb from "components/TopHeader/BreadCrumb";
import TopNavbar from "components/TopHeader/TopNavbar";
import LoadingComponent from "components/UI/LoadingComponent";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { createQRCode, downloadQRCode, getOrderDetails } from "services/order.services";
import {
  setOrderDetailsData,
  toggleActionableOrderId as toggleActionableOrderIdFromOrder,
  setSelectedTierLists,
  toggleShowNoFileModal,
  toggleShowQRCodeCreateModal,
  toggleIsOrderCreateModalOpen,
  toggleIsOrderEditable as toggleIsOrderEditableFromOrder,
} from "store/orderSlice";
import { formatLocation, formattedDate, removeQueryParam, truncateString } from "utils/helper";
import BorderDesign from "components/UI/BorderDesign";
import CardChipButton from "components/UI/CardChipButton";
import ArticleCarousel from "./ArticleCarousel";
import Modal from "components/UI/Modal";
import CreateQRScan from "./CreateQRScan";
import toast from "react-hot-toast";
import useCustomToaster from "hooks/useCustomToaster";
import CreateArticle from "pages/Workspace/Article/CreateArticle";
import {
  setArticleDetails,
  toggleActionableArticleId,
  toggleIsArticleCreateModalOpen,
  toggleIsArticleEditable,
} from "store/articleSlice";
import CardChipButtonTwo from "components/UI/CardChipButtonTwo";
import OrderMapCard from "./OrderMapCard";
import NoFileModal from "./NoFileModal";
import usePermission from "hooks/usePermission";
import CommonDiscardButtonUpdated from "components/UI/CommonDiscardButtonUpdated";
import AddOrder from "../AddOrder";

import { PlanIcon, Shock } from "assests";

const OrderDetailsPage = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { showCustomToast } = useCustomToaster();
  const { hasPermission, userPermission } = usePermission();
  const { orderDetailsData, showQRCodeCreateModal, showNoFileModal, isOrderCreateModalOpen } =
    useSelector((state) => state.orderSlice);
  const { user, currentWorkspace, subscription, subscription_status } = useSelector(
    (state) => state.authenticationSlice,
  );
  const { isArticleEditable, isArticleCreateModalOpen, actionableArticelId, articleDetails } =
    useSelector((state) => state.articleSlice);
  const { orderId, workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    data: orderDetailsQuery,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["orders", "getOrderDetails", orderId],
    queryFn: () => getOrderDetails(orderId),
    enabled: !!orderId,
  });

  useEffect(() => {
    if (
      !isLoading &&
      orderDetailsQuery &&
      orderDetailsQuery?.data &&
      orderDetailsQuery?.data?.order &&
      orderDetailsQuery?.data?.order?.transparency
    ) {
      const transparency = orderDetailsQuery?.data?.order?.transparency;
      let tierData = {};
      if (transparency) {
        for (let i = 0; i < 5; i++) {
          if (!tierData[`tier_${i}`]) {
            tierData[`tier_${i}`] = [];
          }
          if (transparency[`tier_${i}`] && transparency[`tier_${i}`].length > 0) {
            transparency[`tier_${i}`].map((item) =>
              tierData[`tier_${i}`].push({ label: item?.name, value: item?.id }),
            );
          }
        }
      }
      dispatch(setSelectedTierLists(tierData));
      dispatch(setOrderDetailsData(orderDetailsQuery?.data?.order));
    }
  }, [orderDetailsQuery, isLoading]);

  const convertSeason = (season) => {
    if (season.includes("/")) {
      return season
        .split("/")
        .map((part) => part.trim()) // Optional: trim whitespace
        .join(" / "); // Add spaces around slash
    }
    return season;
  };

  const createQRCodeMutation = useMutation({
    mutationKey: ["qrcode", "createQrcode"],
    mutationFn: (data) => {
      return createQRCode(data);
    },
    onMutate: () => {
      showCustomToast(
        {
          title: "Creating QR...",
        },
        "createQrcode",
      );
    },
    onSuccess: (res) => {
      toast.dismiss("createQrcode");
      showCustomToast({
        title: "QR code has been created",
      });
      closeModal();
      refetch();
      queryClient.invalidateQueries("protectedResource");
    },
    onError: (error) => {
      toast.dismiss("createQrcode");
      const errormessage = error.response?.data?.message || error.message || "Something went wrong";
      showCustomToast({
        title: errormessage,
      });
    },
  });
  const downloadQRCodeMutation = useMutation({
    mutationKey: ["download", "downloadQrcode"],
    mutationFn: (data) => {
      return downloadQRCode(data);
    },
    onMutate: () => {
      showCustomToast(
        {
          title: "Downloading...",
        },
        "downloadQrcode",
      );
    },
    onSuccess: (res) => {
      toast.dismiss("downloadQrcode");
      showCustomToast({
        title: "QR code has been downloaded",
      });
      closeModal();
      refetch();
      queryClient.invalidateQueries("downloadQrcode");
    },
    onError: (error) => {
      toast.dismiss("downloadQrcode");
      const errormessage = error.response?.data?.message || error.message || "Something went wrong";
      showCustomToast({
        title: errormessage,
      });
    },
  });
  const handleCreateQRCode = () => {
    createQRCodeMutation.mutate({ order_id: parseInt(orderId) });
  };

  const handleImageDownload = async (url) => {
    const filename = url.split("/").pop(); // Extract the filename from the URL
    downloadQRCodeMutation.mutate(filename);
  };

  const [activeTab, setActiveTab] = useState("traceability");
  const [selectedArticles, setSelectedArticles] = useState([]);
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    setSelectedArticles([]);
    removeQueryParam(["selectedTab"], location, navigate);
  };

  const handleClickOptionButton = (articleId, actionType) => {
    switch (actionType) {
      case "edit":
        dispatch(toggleActionableArticleId(articleId));
        if (!isArticleEditable) {
          dispatch(toggleIsArticleEditable());
        }
        dispatch(toggleIsArticleCreateModalOpen());
        break;
      case "view":
        dispatch(toggleActionableArticleId(articleId));
        dispatch(toggleIsArticleCreateModalOpen());
        if (isArticleEditable) {
          dispatch(toggleIsArticleEditable());
        }
        break;
      default:
        break;
    }
  };

  const handleArticleActions = (orderDetailsData) => {
    if (
      hasPermission("updateArticle") &&
      parseInt(user?.workspace_id) ===
        parseInt(orderDetailsData?.article?.created_by_user?.own_workspace_id)
    ) {
      handleClickOptionButton(orderDetailsData?.articles_id, "edit");
    } else {
      handleClickOptionButton(orderDetailsData?.articles_id, "view");
    }
    handleTabClick("article_details");
  };

  const closeModal = () => {
    if (isArticleCreateModalOpen) {
      dispatch(toggleIsArticleCreateModalOpen());
    }
    if (showQRCodeCreateModal) {
      dispatch(toggleShowQRCodeCreateModal());
    }
    if (showNoFileModal) {
      dispatch(toggleShowNoFileModal());
    }
    if (actionableArticelId) {
      dispatch(toggleActionableArticleId());
    }
    if (articleDetails) {
      dispatch(setArticleDetails());
    }
    if (isArticleEditable) {
      dispatch(toggleIsArticleEditable());
    }
    if (isOrderCreateModalOpen) {
      dispatch(toggleIsOrderCreateModalOpen());
    }
    setActiveTab("traceability");
    removeQueryParam(
      ["fromGlobalCreate", "fromGlobalSearch", "article_id", "selectedTab"],
      location,
      navigate,
    );
    removeQueryParam(["selectedTab"], location, navigate);
  };

  const handleViewDetailsClick = (id, type = "supplier") => {
    navigate(`/workspace/${workspaceId}/supplier-details/${id}/${type}`, { replace: true });
  };

  const getSupplierName = (orderDetailsData) => {
    let status = "N/A";
    let supplierWorkspaceId;
    if (orderDetailsData?.supplier) {
      status = orderDetailsData.supplier.status;
      supplierWorkspaceId = parseInt(orderDetailsData.supplier.id);
    } else if (
      orderDetailsData?.company &&
      orderDetailsData.company.purchased_package === "supplier"
    ) {
      status = orderDetailsData.company.status;
      supplierWorkspaceId = parseInt(orderDetailsData.company.id);
    } else if (
      orderDetailsData?.workspace &&
      orderDetailsData.workspace.purchased_package === "supplier"
    ) {
      status = orderDetailsData.workspace.status;
      supplierWorkspaceId = parseInt(orderDetailsData.workspace.id);
    }

    return {
      status,
      supplierWorkspaceId,
    };
  };

  const { refetchBillingData, workspacePlan } = useSelector((state) => state.billingSlice);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const openUpgradeModal = () => {
    setShowUpgradeModal(true);
  };
  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
  };
  const [showUpgradeModalQRCode, setShowUpgradeModalQRCode] = useState(false);
  const openUpgradeModalQRCode = () => {
    setShowUpgradeModalQRCode(true);
  };
  const closeUpgradeModalQRCode = () => {
    setShowUpgradeModalQRCode(false);
  };
  return (
    <>
      {isLoading || orderDetailsData === null ? (
        <LoadingComponent />
      ) : !isLoading && orderDetailsData ? (
        <>
          <TopNavbar isFromDashboard={false}>
            <BreadCrumb text={orderDetailsData?.order_number} textAnother="My Orders" />
          </TopNavbar>
          <div className="pl-4 md:pl-8  pt-[64px] md:pt-[108px] ">
            <div className="block md:hidden pt-4">
              <BreadCrumb text={orderDetailsData?.order_number} textAnother="My Orders" />
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 xl:col-span-7 pr-4 md:pr-8 xl:pr-0">
                <div className="flex flex-col gap-4 md:gap-0 md:flex-row justify-between items-start">
                  <div className="justify-start text-2xl md:text-5xl font-semibold md:leading-[60px] text-global-title break-words">
                    {orderDetailsData?.article?.article_title}
                  </div>
                  <div>
                    <div className="flex flex-col md:flex-col items-stretch md:items-start gap-2 w-full">
                      {userPermission("update_order.can") && (
                        <BorderDesign
                          className={`w-[calc(100vw-32px)] md:w-11 h-11 !rounded-lg !border !border-border-secondary bg-global-theme !shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] ${
                            orderDetailsData?.status?.name === "active"
                              ? "cursor-pointer"
                              : "cursor-not-allowed"
                          }`}
                          boxShadowShow={true}
                          role="button"
                          onClick={() => {
                            if (orderDetailsData?.status?.name === "active") {
                              dispatch(toggleActionableOrderIdFromOrder(orderId));
                              dispatch(toggleIsOrderEditableFromOrder());
                              dispatch(toggleIsOrderCreateModalOpen());
                            }
                          }}
                        >
                          <EditPen
                            width={20}
                            height={20}
                            strokeColor="var(--color-global-paragraph-3)"
                          />
                        </BorderDesign>
                      )}

                      {(() => {
                        const qrStatus = userPermission("access_qr_code.can");
                        const qrModal = userPermission("access_qr_code.modal");

                        // 1️⃣ Show download button if QR code exists
                        if (orderDetailsData.qr && orderDetailsData.qrcode?.url && qrStatus) {
                          return (
                            <BorderDesign
                              role="button"
                              className="w-[calc(100vw-32px)] md:w-11 h-11 !rounded-lg !border cursor-pointer !border-border-secondary bg-global-theme !shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
                              boxShadowShow={true}
                              onClick={() => handleImageDownload(orderDetailsData.qr)}
                            >
                              <DownloadIcon width={24} height={24} />
                            </BorderDesign>
                          );
                        }

                        // 2️⃣ Show QR create button if permission is true
                        if (qrStatus === true) {
                          return (
                            <BorderDesign
                              role="button"
                              className="w-[calc(100vw-32px)] md:w-11 h-11 !rounded-lg !border cursor-pointer !border-border-secondary bg-global-theme !shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
                              boxShadowShow={true}
                              onClick={() => {
                                if (
                                  orderDetailsData?.article?.file &&
                                  orderDetailsData.article.file.length > 0
                                ) {
                                  dispatch(toggleShowQRCodeCreateModal());
                                } else {
                                  dispatch(toggleShowNoFileModal());
                                }
                              }}
                            >
                              <QrscanIcon width={24} height={24} />
                            </BorderDesign>
                          );
                        }

                        // 3️⃣ Show upgrade modal if permission denied but modal allowed
                        if (qrStatus === false && qrModal === true) {
                          return (
                            <BorderDesign
                              role="button"
                              className="w-[calc(100vw-32px)] md:w-11 h-11 !rounded-lg !border cursor-pointer !border-border-secondary bg-global-theme !shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]"
                              boxShadowShow={true}
                              onClick={openUpgradeModalQRCode}
                            >
                              <QrscanIcon width={24} height={24} />
                            </BorderDesign>
                          );
                        }

                        return null;
                      })()}
                    </div>
                  </div>
                </div>
                <div className="pt-2.5">
                  <div className="justify-start text-global-title text-xl md:text-2xl font-semibold leading-loose">
                    Overview
                  </div>
                  <div className="pt-6 md:pt-16 inline-flex flex-wrap  justify-start items-center gap-6 md:gap-16">
                    <div className="inline-flex flex-col justify-start items-start gap-6 md:gap-8">
                      <CardChipButton
                        isStatus={true}
                        title="STATUS"
                        data={orderDetailsData?.status?.name}
                      />
                      <CardChipButton
                        title="QUANTITY"
                        data={
                          orderDetailsData.quantity != null
                            ? truncateString(orderDetailsData.quantity)
                            : "N/A"
                        }
                      />
                      <CardChipButton
                        title="SEX"
                        data={
                          orderDetailsData.article &&
                          orderDetailsData.article.gender &&
                          orderDetailsData.article.gender.name &&
                          truncateString(orderDetailsData.article.gender.name)
                        }
                      />
                    </div>
                    <div className="inline-flex flex-col justify-start items-start gap-6 md:gap-8">
                      <CardChipButton
                        title="ARTICLE ID"
                        data={
                          orderDetailsData.article &&
                          orderDetailsData.article.article_id &&
                          truncateString(orderDetailsData.article.article_id)
                        }
                      />
                      <CardChipButton
                        title="ORDER DATE"
                        data={
                          orderDetailsData?.order_date &&
                          formattedDate(orderDetailsData?.order_date)
                        }
                      />
                      <CardChipButton
                        title="QR EXPERIENCE"
                        data={orderDetailsData?.qr ? "Enabled" : "Disabled"}
                        isDisabled={orderDetailsData?.qr ? false : true}
                        isStatus={orderDetailsData?.qr ? true : false}
                      />
                    </div>
                    <div className="inline-flex flex-col justify-start items-start gap-6 md:gap-8">
                      <CardChipButton
                        title="ORDER NUMBER"
                        data={
                          orderDetailsData?.order_number &&
                          truncateString(orderDetailsData?.order_number)
                        }
                      />
                      <CardChipButton
                        title="SEASON"
                        data={
                          orderDetailsData.season &&
                          orderDetailsData.season.name &&
                          `${convertSeason(orderDetailsData.season.name)}${" "}${orderDetailsData?.season?.year}`
                        }
                      />
                      <CardChipButton
                        title="SUPPLIER"
                        data={
                          orderDetailsData?.supplier
                            ? orderDetailsData?.supplier?.name
                            : orderDetailsData?.company &&
                                orderDetailsData?.company?.purchased_package === "supplier"
                              ? orderDetailsData?.company?.name
                              : orderDetailsData?.workspace &&
                                  orderDetailsData?.workspace?.purchased_package === "supplier"
                                ? orderDetailsData?.workspace?.name
                                : "N/A"
                        }
                      />
                    </div>
                  </div>
                  <div className="pt-8 md:pt-[125px]">
                    <div className="overflow-x-auto ">
                      <div
                        className="mb-1 md:mb-0 flex items-center justify-between bg-transparent text-sm font-semibold text-global-paragraph-3 border border-global-border-extra divide-x divide-global-border-extra rounded-md w-[379px] whitespace-nowrap h-10 mt-4 lg:mt-0"
                        style={{
                          boxShadow:
                            "0px 0px 0px 1px rgba(16, 24, 40, 0.18) inset, 0px -2px 0px 0px rgba(16, 24, 40, 0.05) inset, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                        }}
                      >
                        <button
                          className={`w-full flex justify-center items-center text-center text-sm  px-2 lg:px-4 h-[38px] rounded-l-md bg-global-theme hover:bg-global-white ${activeTab === "traceability" ? "bg-global-white" : "bg-global-theme"}`}
                          onClick={() => {
                            handleTabClick("traceability");
                          }}
                        >
                          Traceability
                        </button>
                        <button
                          className={` flex justify-center items-center text-center text-sm   px-2 lg:px-4 h-[38px] tracking-[0.0015em] bg-global-theme hover:bg-global-white ${activeTab === "article_details" ? "bg-global-white" : "bg-global-theme"}`}
                          onClick={() => handleArticleActions(orderDetailsData)}
                        >
                          Article Details
                        </button>
                        <button
                          className={`w-full flex justify-center items-center text-center text-sm  px-2 lg:pl-4 tracking-[0.002em] lg:pr-[14px] h-[38px] rounded-r-md bg-global-theme hover:bg-global-white disabled:bg-global-theme disabled:cursor-not-allowed ${activeTab === "supplier_details" ? "bg-global-white" : "bg-global-theme"}`}
                          disabled={getSupplierName(orderDetailsData)?.status !== "active"}
                          onClick={() => {
                            handleTabClick("supplier_details");
                            handleViewDetailsClick(
                              getSupplierName(orderDetailsData)?.supplierWorkspaceId,
                              getSupplierName(orderDetailsData)?.status,
                            );
                          }}
                        >
                          Supplier Details
                        </button>
                      </div>
                    </div>
                    {activeTab === "Articles" ? <></> : activeTab === "Templates" && null}
                  </div>
                </div>
              </div>
              <div className="col-span-12 xl:col-span-5">
                <div className="relative mx-auto w-full">
                  <ArticleCarousel
                    imagePreviews={orderDetailsData?.article?.file}
                    qrcode={orderDetailsData?.qr}
                  />
                </div>
              </div>
            </div>
            <div className="pr-4 md:pr-8">
              <div className="pt-4 md:pt-6  ">
                <p className="text-2xl md:text-[30px] md:leading-[38px] font-semibold text-global-title whitespace-nowrap">
                  Traceability
                </p>
                <div className="overflow-x-auto pt-4  mb-5">
                  <div className="flex justify-start items-center gap-2 mb-1 md:mb-0">
                    <CardChipButtonTwo>
                      From:{" "}
                      {orderDetailsData?.supplier
                        ? formatLocation({
                            region: orderDetailsData?.supplier?.region,
                            country: orderDetailsData?.supplier?.country,
                          })
                        : orderDetailsData?.company &&
                            orderDetailsData?.company?.purchased_package === "supplier"
                          ? formatLocation({
                              region: orderDetailsData?.company?.region,
                              country: orderDetailsData?.company?.country,
                            })
                          : orderDetailsData?.workspace &&
                              orderDetailsData?.workspace?.purchased_package === "supplier"
                            ? formatLocation({
                                region: orderDetailsData?.workspace?.region,
                                country: orderDetailsData?.workspace?.country,
                              })
                            : "N/A"}
                    </CardChipButtonTwo>
                    <div>
                      <ArrowRight
                        width={20}
                        height={20}
                        fillColor={"var(--color-global-title-3)"}
                      />
                    </div>
                    <CardChipButtonTwo>
                      To:{" "}
                      {orderDetailsData?.company &&
                      (orderDetailsData?.company?.purchased_package === "retailer" ||
                        orderDetailsData?.company?.purchased_package === "importer" ||
                        orderDetailsData?.company?.purchased_package === "brand")
                        ? formatLocation({
                            region: orderDetailsData?.company?.region,
                            country: orderDetailsData?.company?.country,
                          })
                        : orderDetailsData?.workspace &&
                            (orderDetailsData?.workspace?.purchased_package === "retailer" ||
                              orderDetailsData?.workspace?.purchased_package === "importer" ||
                              orderDetailsData?.workspace?.purchased_package === "brand")
                          ? formatLocation({
                              region: orderDetailsData?.workspace?.region,
                              country: orderDetailsData?.workspace?.country,
                            })
                          : "N/A"}
                    </CardChipButtonTwo>
                  </div>
                </div>
              </div>
              <div className="flex  flex-col lg:flex-row gap-4 md:gap-6">
                <div className="w-full">
                  <OrderMapCard orderDetailsData={orderDetailsData} refetch={refetch} />
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <EmptyComponent text="You don’t have any Details yet." icon={false} />
      )}

      <Modal open={showQRCodeCreateModal} close={closeModal}>
        <CreateQRScan closeModal={closeModal} handleCreateQRCode={handleCreateQRCode} />
      </Modal>
      <Modal open={showNoFileModal} close={closeModal}>
        <NoFileModal closeModal={closeModal} />
      </Modal>
      <Modal open={isOrderCreateModalOpen} close={closeModal}>
        <AddOrder
          closeModal={closeModal}
          refetchData={refetch}
          setPage={() => {}}
          status={orderDetailsData?.status?.name}
          isFromActiveOrder={true}
        />
      </Modal>
      <Modal open={isArticleCreateModalOpen} close={closeModal}>
        <CreateArticle
          closeModal={closeModal}
          setActiveTab={setActiveTab}
          refetchArticleData={refetch}
        />
      </Modal>
      <Modal open={showUpgradeModal} close={closeUpgradeModal}>
        <CommonDiscardButtonUpdated
          closeModal={closeUpgradeModal}
          title="Upgrade plan"
          subText1={
            currentWorkspace && !currentWorkspace?.is_owner && !workspacePlan
              ? `Your partner workspace current plan does not allow this action. Ask the workspace owner to upgrade the plan to enable this feature.`
              : `You current plan does not allow this action. Upgrade your plan to enable this feature.`
          }
          className="max-w-[544px]"
          confirmButtonClassName="w-full h-11"
          IconComponent={PlanIcon}
          confirmButtonText={
            currentWorkspace && !currentWorkspace?.is_owner && !workspacePlan ? null : "Ugrade Plan"
          }
          confirmButtonVariant="primary"
          onConfirmClick={() => {
            navigate(`/workspace/${currentWorkspace?.user_workspace_id}/setting?selectedTab=plan`);
            if (showUpgradeModal) {
              setShowUpgradeModal(false);
            }
          }}
          icon={
            <Shock width={20} height={20} strokeColor={"var(--color-icon-fill-secondary-button)"} />
          }
        />
      </Modal>
      {/* upgrade modal for qr code */}
      <Modal open={showUpgradeModalQRCode} close={closeUpgradeModalQRCode}>
        <CommonDiscardButtonUpdated
          closeModal={closeUpgradeModalQRCode}
          title="Upgrade plan"
          subText1={
            currentWorkspace && !currentWorkspace?.is_owner
              ? `Your partner workspace current plan does not allow this action. Ask the workspace owner to upgrade the plan to enable this feature.`
              : `You current plan does not allow this action. Upgrade your plan to enable this feature.`
          }
          className="max-w-[544px]"
          confirmButtonClassName="w-full h-11"
          IconComponent={PlanIcon}
          confirmButtonText={currentWorkspace && !currentWorkspace?.is_owner ? null : "Ugrade Plan"}
          confirmButtonVariant="primary"
          onConfirmClick={() => {
            navigate(`/workspace/${currentWorkspace?.user_workspace_id}/setting?selectedTab=plan`);
            if (showUpgradeModal) {
              setShowUpgradeModal(false);
            }
          }}
          icon={
            <Shock width={20} height={20} strokeColor={"var(--color-icon-fill-secondary-button)"} />
          }
        />
      </Modal>
    </>
  );
};

export default OrderDetailsPage;
